import { Images } from "../../../Images";
import TileMinedLogic from "../../../logic/TileMinedLogic";
import LaserSkillRenderer from "../../../view/canvas/LaserSkillRenderer";
import SkillRenderer from "../../../view/canvas/SkillRenderer";
import Crew from "../../crew/Crew";
import Totals from "../../crew/Totals";
import WorldGrid from "../../grid/WorldGrid";
import BigNum from "../../math/BigNum";
import Laser from "./Laser";
import GervaisAnimatedSpritesheet from "../../sprite/GervaisAnimatedSpritesheet";
import NumberValue from "../../value/NumberValue";
import AddMinerSkillNode from "../common/AddMInerSkillNode";
import { SkillIds } from "../SkillIds";
import SkillType from "../SkillType";
import VectorField from "../../grid/VectorField";
import PartyCharacter from "../../character/PartyCharacter";
import CoolDownSkillNode from "../common/CoolDownSkillNode";
import DurationSkillNode from "../common/DurationSkillNode";
import GameTime from "../../time/GameTime";
import TileRangeSkillNode from "../common/TileRangeSkillNode";
import { Settings } from "../../../Settings";

export default class LaserSkill extends SkillType {
    /**
     * @param {WorldGrid} worldGrid 
     * @param {Totals} totals
     * @param {TileMinedLogic} tileMinedLogic 
     * @param {Crew} crew
     * @param {GameTime} gameTime
     * @param {GervaisAnimatedSpritesheet} spellFxSpritesheet
     * @param {VectorField} vectorField
     */
    constructor(worldGrid, totals, tileMinedLogic, crew, gameTime, spellFxSpritesheet, vectorField) {
        super(SkillIds.LASER);

        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {TileMinedLogic}
         */
        this.tileMinedLogic = tileMinedLogic;

        /**
         * @constant
         * @type {Totals}
         */
        this.totals = totals;
        /**
         * @constant
         * @type {Crew}
         */
        this.crew = crew;
        /**
         * @constant
         * @type {GameTime}
         */
        this.gameTime = gameTime;
        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = vectorField;

        /**
         * @constant
         * @type {NumberValue}
         */
        this.laserRangeUpgrades = new NumberValue(null, 0);

        /**
         * @constant
         * @type {Laser[]}
         */
        this.laserArray = [];

        /**
         * @constant
         * @type {Object}
         */
        this.laserByCharacter = {};

        /**
         * @constant
         * @type {LaserSkillRenderer}
         */
        this.renderer = new LaserSkillRenderer(this.laserArray, spellFxSpritesheet);

        let cost = 1;

        const addMinerNode1 = this.addNode(new AddMinerSkillNode('addLaserMiner1', '+1 Laser Miner', 'Blasts stone with a laser', 
            totals, Images.skill.laser, new BigNum(cost + Settings.skill.costs.addLaser1), this, crew, SkillIds.LASER
        ));
        
        ////////////////////////////
        // Cost Tier 2
        ////////////////////////////

        cost++;

        const rangeBonus1 = this.addNode(new TileRangeSkillNode('laserRange1', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const coolBonus1 = this.addNode(new CoolDownSkillNode('laserCool1', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus1 = this.addNode(new DurationSkillNode('laserDur1', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 3
        ////////////////////////////
        
        cost++;

        const rangeBonus2 = this.addNode(new TileRangeSkillNode('laserRange2', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const coolBonus2 = this.addNode(new CoolDownSkillNode('laserCool2', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus2 = this.addNode(new DurationSkillNode('laserDur2', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 4
        ////////////////////////////
        
        cost++;

        const rangeBonus3 = this.addNode(new TileRangeSkillNode('laserRange3', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const coolBonus3 = this.addNode(new CoolDownSkillNode('laserCool3', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus3 = this.addNode(new DurationSkillNode('laserDur3', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 5
        ////////////////////////////
        
        cost++;

        const rangeBonus4 = this.addNode(new TileRangeSkillNode('laserRange4', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const addMinerNode2 = this.addNode(new AddMinerSkillNode('addLaserMiner2', '+1 Laser Miner', 'Blasts stone with a laser', 
            totals, Images.skill.laser, new BigNum(cost + Settings.skill.costs.addMiner2), this, crew, SkillIds.LASER
        ));

        const coolBonus4 = this.addNode(new CoolDownSkillNode('laserCool4', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus4 = this.addNode(new DurationSkillNode('laserDur4', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 6
        ////////////////////////////
        
        cost++;

        const rangeBonus5 = this.addNode(new TileRangeSkillNode('laserRange5', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const coolBonus5 = this.addNode(new CoolDownSkillNode('laserCool5', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus5 = this.addNode(new DurationSkillNode('laserDur5', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 7
        ////////////////////////////
        
        cost++;

        const rangeBonus6 = this.addNode(new TileRangeSkillNode('laserRange6', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const coolBonus6 = this.addNode(new CoolDownSkillNode('laserCool6', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus6 = this.addNode(new DurationSkillNode('laserDur6', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 8
        ////////////////////////////
        
        cost++;

        const rangeBonus7 = this.addNode(new TileRangeSkillNode('laserRange7', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const addMinerNode3 = this.addNode(new AddMinerSkillNode('addLaserMiner3', '+1 Laser Miner', 'Blasts stone with a laser', 
            totals, Images.skill.laser, new BigNum(cost + Settings.skill.costs.addMiner3), this, crew, SkillIds.LASER
        ));

        const coolBonus7 = this.addNode(new CoolDownSkillNode('laserCool7', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus7 = this.addNode(new DurationSkillNode('laserDur7', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 9
        ////////////////////////////
        
        cost++;

        const rangeBonus8 = this.addNode(new TileRangeSkillNode('laserRange8', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const coolBonus8 = this.addNode(new CoolDownSkillNode('laserCool8', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus8 = this.addNode(new DurationSkillNode('laserDur8', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 10
        ////////////////////////////

        cost++;

        const rangeBonus9 = this.addNode(new TileRangeSkillNode('laserRange9', 
            totals, Images.skill.laser, new BigNum(cost), this, Settings.skill.laser.tilesPerUpgrade, this.laserRangeUpgrades, Settings.skill.laser.minRange
        ));

        const addMinerNode4 = this.addNode(new AddMinerSkillNode('addLaserMiner4', '+1 Laser Miner', 'Blasts stone with a laser', 
            totals, Images.skill.laser, new BigNum(cost + Settings.skill.costs.addMiner4), this, crew, SkillIds.LASER
        ));

        const coolBonus9 = this.addNode(new CoolDownSkillNode('laserCool9', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus9 = this.addNode(new DurationSkillNode('laserDur9', 
            totals, Images.skill.laser, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ///////////////////////////////////
        // Setup node relationships
        ///////////////////////////////////

        addMinerNode1.addChildNode(rangeBonus1);
        
        rangeBonus1.addChildNode(rangeBonus2);
        rangeBonus2.addChildNode(rangeBonus3);
        rangeBonus3.addChildNode(rangeBonus4);
        rangeBonus4.addChildNode(rangeBonus5);
        rangeBonus5.addChildNode(rangeBonus6);
        rangeBonus6.addChildNode(rangeBonus7);
        rangeBonus7.addChildNode(rangeBonus8);
        rangeBonus8.addChildNode(rangeBonus9);

        rangeBonus1.addChildNode(coolBonus1);
        rangeBonus2.addChildNode(coolBonus2);
        rangeBonus3.addChildNode(coolBonus3);
        rangeBonus4.addChildNode(coolBonus4);
        rangeBonus5.addChildNode(coolBonus5);
        rangeBonus6.addChildNode(coolBonus6);
        rangeBonus7.addChildNode(coolBonus7);
        rangeBonus8.addChildNode(coolBonus8);
        rangeBonus9.addChildNode(coolBonus9);

        coolBonus1.addChildNode(durationBonus1);
        coolBonus2.addChildNode(durationBonus2);
        coolBonus3.addChildNode(durationBonus3);
        coolBonus4.addChildNode(durationBonus4);
        coolBonus5.addChildNode(durationBonus5);
        coolBonus6.addChildNode(durationBonus6);
        coolBonus7.addChildNode(durationBonus7);
        coolBonus8.addChildNode(durationBonus8);
        coolBonus9.addChildNode(durationBonus9);

        rangeBonus3.addChildNode(addMinerNode2);
        rangeBonus7.addChildNode(addMinerNode3);
        rangeBonus9.addChildNode(addMinerNode4);

        ///////////////////////////////////
        // Position the nodes in the graph
        ///////////////////////////////////

        // Starting coordinate of the skill
        const startX = 3;
        const startY = 8;

        addMinerNode1.setPosition(startX, startY);

        rangeBonus1.setPosition(startX + 1, startY);
        rangeBonus2.setPosition(startX + 2, startY);
        rangeBonus3.setPosition(startX + 3, startY);
        rangeBonus4.setPosition(startX + 4, startY);
        rangeBonus5.setPosition(startX + 5, startY);
        rangeBonus6.setPosition(startX + 6, startY);
        rangeBonus7.setPosition(startX + 7, startY);
        rangeBonus8.setPosition(startX + 8, startY);
        rangeBonus9.setPosition(startX + 9, startY);

        coolBonus1.setPosition(startX + 1, startY - 1);
        coolBonus2.setPosition(startX + 2, startY - 1);
        coolBonus3.setPosition(startX + 3, startY - 1);
        coolBonus4.setPosition(startX + 4, startY - 1);
        coolBonus5.setPosition(startX + 5, startY - 1);
        coolBonus6.setPosition(startX + 6, startY - 1);
        coolBonus7.setPosition(startX + 7, startY - 1);
        coolBonus8.setPosition(startX + 8, startY - 1);
        coolBonus9.setPosition(startX + 9, startY - 1);

        durationBonus1.setPosition(startX + 1, startY - 2);
        durationBonus2.setPosition(startX + 2, startY - 2);
        durationBonus3.setPosition(startX + 3, startY - 2);
        durationBonus4.setPosition(startX + 4, startY - 2);
        durationBonus5.setPosition(startX + 5, startY - 2);
        durationBonus6.setPosition(startX + 6, startY - 2);
        durationBonus7.setPosition(startX + 7, startY - 2);
        durationBonus8.setPosition(startX + 8, startY - 2);
        durationBonus9.setPosition(startX + 9, startY - 2);

        addMinerNode2.setPosition(startX + 3, startY + 1); // below rangeBonus4
        addMinerNode3.setPosition(startX + 7, startY + 1); // below rangeBonus7
        addMinerNode4.setPosition(startX + 9, startY + 1); // below rangeBonus9

    }

    /**
     * @override
     * @param {PartyCharacter} miner
     * @param {number} frameTimeRatio 
     * @param {boolean} skillActive
     */
    updateSkillForFrame(miner, frameTimeRatio, skillActive) {

        /**
         * @type {Laser}
         */
        let laser = this.laserByCharacter[miner.id];
        if (!laser) {
            laser = new Laser(this.worldGrid, this.gameTime, this.vectorField, this.tileMinedLogic, this.laserRangeUpgrades);
            this.laserByCharacter[miner.id] = laser;
            this.laserArray.push(laser);
        }

        if (skillActive && !laser.skillActive) {
            laser.onSkillActivation();
        }
        else if (!skillActive && laser.skillActive) {
            laser.onSkillDeactivation();
        }

        if (skillActive) {
            laser.updateForFrame(miner, frameTimeRatio);
        }
    }

    /**
     * @override
     * @returns {SkillRenderer}
     */
    getRenderer() {
        return this.renderer;
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        super.resetFull();

        this.laserArray.length = 0;
        this.laserByCharacter = {};

        this.laserRangeUpgrades.reset();
    }
}